import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number using compact notation:
 *   1234      → "1.2K"
 *   1_500_000 → "1.5M"
 *   2_300_000_000 → "2.3B"
 *   999       → "999"
 *
 * `decimals` controls fractional digits (default 1).
 * Trailing ".0" is stripped automatically.
 */
export function formatNumber(n: number, decimals = 1): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return sign + strip(abs / 1_000_000_000, decimals) + "B";
  if (abs >= 1_000_000) return sign + strip(abs / 1_000_000, decimals) + "M";
  if (abs >= 1_000) return sign + strip(abs / 1_000, decimals) + "K";
  if (Number.isInteger(n)) return n.toString();
  return n.toFixed(decimals);
}

function strip(v: number, d: number): string {
  const s = v.toFixed(d);
  // Remove trailing .0 but keep .5 etc.
  return d > 0 && s.endsWith("0".repeat(d)) ? v.toFixed(0) : s;
}

/**
 * Format a credit / currency amount.
 * Small values keep precision; large values use compact K/M/B.
 */
export function formatCredits(n: number): string {
  if (Math.abs(n) >= 1_000) return formatNumber(n, 1);
  return n % 1 === 0 ? n.toString() : n.toFixed(2);
}
